import { Images } from '../../../../../../../../../shared/app-constants';
import { SubscriptionPlanTitle } from '../../../../../../../../../shared/utils/subscription-plans';
import { GetPlansResponse } from '../../../../../types';

function formatCreditAmount(amount: number) {
  return amount.toLocaleString('en-US');
}

function buildLeadFinderFeatures({
  credits,
  cadenceLabel,
  planName,
}: {
  credits: number;
  cadenceLabel: 'monthly' | 'yearly';
  planName: string;
}) {
  const differentLabels = [];

  if (planName.toLowerCase().includes('starter')) {
    differentLabels.push({
      label: '60+ Search Filters',
    });
  } else if (planName.toLowerCase().includes('pro')) {
    differentLabels.push({
      label: '70+ Advanced Search Filters',
    });
  }
  const features = [
    {
      label:
        cadenceLabel === 'yearly'
          ? `${formatCreditAmount(credits)} credits granted upfront yearly`
          : `${formatCreditAmount(credits)} credits ${cadenceLabel}`,
      labelClass: 'font-semibold',
      highlight: true,
    },
    {
      label: 'Real-time Verified Contacts',
    },
    {
      label: 'Work Email Included',
    },
    {
      label: 'Access 30+ rich data fields along with emails',
      labelClass: 'break-lines',
    },
    {
      label: 'People-Level Database Search',
    },
    {
      label: 'Company-Level Database Search',
    },
    {
      label: 'Waterfall Enrichment',
      tooltipText: 'Aggregated from multiple trusted providers',
    },
    ...differentLabels,
    {
      label: 'Reveal Email & Phone Information',
      tooltipText: '1 credit for Email, 7 credits for Email + Phone',
    },
    {
      label: 'Manage Prospects & Accounts in Saleshandy CRM',
    },
    {
      label: 'Unused Credits Rollover',
      tooltipText: '50% rollover to next billing cycle',
    },
  ];

  return features;
}

function getLeadFinderMonthlyCredits() {
  return {
    starter: 2500,
    pro: 4000,
  };
}

function getLeadFinderYearlyCredits(monthlyCredits: {
  starter: number;
  pro: number;
}) {
  return {
    starter: monthlyCredits.starter * 12,
    pro: monthlyCredits.pro * 12,
  };
}

export function getLeadFinderCredits() {
  const monthly = getLeadFinderMonthlyCredits();
  return {
    monthly,
    yearly: getLeadFinderYearlyCredits(monthly),
  };
}

export function getLeadFinderPlanMetaData(planName) {
  const credits = getLeadFinderCredits();

  const starterMonthly = {
    displayName: 'Starter',
    features: buildLeadFinderFeatures({
      credits: credits.monthly.starter,
      cadenceLabel: 'monthly',
      planName,
    }),
  };

  const proMonthly = {
    displayName: 'Pro',
    features: buildLeadFinderFeatures({
      credits: credits.monthly.pro,
      cadenceLabel: 'monthly',
      planName,
    }),
    isBestValuePlan: true,
  };

  const starterYearly = {
    displayName: 'Starter',
    features: buildLeadFinderFeatures({
      credits: credits.yearly.starter,
      cadenceLabel: 'yearly',
      planName,
    }),
  };

  const proYearly = {
    displayName: 'Pro',
    features: buildLeadFinderFeatures({
      credits: credits.yearly.pro,
      cadenceLabel: 'yearly',
      planName,
    }),
    isBestValuePlan: true,
  };

  const planMetaData = {
    [SubscriptionPlanTitle.LeadFinderEmailStarterMonthly]: starterMonthly,
    [SubscriptionPlanTitle.LeadFinderEmailProMonthly]: proMonthly,
    [SubscriptionPlanTitle.LeadFinderEmailStarterYearly]: starterYearly,
    [SubscriptionPlanTitle.LeadFinderEmailProYearly]: proYearly,
    [SubscriptionPlanTitle.LeadFinderStarterMonthly]: starterMonthly,
    [SubscriptionPlanTitle.LeadFinderProMonthly]: proMonthly,

    [SubscriptionPlanTitle.LeadFinderStarterAnnually]: starterYearly,
    [SubscriptionPlanTitle.LeadFinderProAnnually]: proYearly,
  };

  return planMetaData[planName];
}

export const getPlanPrice = (planPrice, planMode) =>
  Number(planPrice) / Number(planMode);

export const getPlanDetailsObj = (plan) => ({
  id: plan.id || null,
  planName: plan.planName,
  displayName: getLeadFinderPlanMetaData(plan.planName)?.displayName,
  isBestValuePlan: getLeadFinderPlanMetaData(plan.planName)?.isBestValuePlan,
  features: getLeadFinderPlanMetaData(plan.planName)?.features,
  price: getPlanPrice(plan.amount, plan.planMode) || null,
  ...plan,
});

export const getLeadFinderPlanMonthlyPrice = (
  planCode: string,
  plans: GetPlansResponse,
) => {
  const correspondingMonthlyPlanCode =
    planCode?.replace('yearly', 'monthly') || '';

  const correspondingMonthlyPlan = plans.find(
    (plan) => plan.planCode === correspondingMonthlyPlanCode,
  );

  if (correspondingMonthlyPlan) {
    return parseInt(correspondingMonthlyPlan?.amount || '0', 10);
  }

  return 0;
};

export const getLeadFinderPlanYearlyPrice = (
  planCode: string,
  plans: GetPlansResponse,
) => {
  const correspondingYearlyPlanCode =
    planCode?.replace('monthly', 'yearly') || '';

  const correspondingYearlyPlan = plans.find(
    (plan) => plan.planCode === correspondingYearlyPlanCode,
  );

  if (correspondingYearlyPlan) {
    return parseInt(correspondingYearlyPlan?.amount || '0', 10);
  }

  return 0;
};

export const getStepWisePlanProspects = (isVersionLocked) =>
  isVersionLocked
    ? {
        step1: '10,000',
        step2: '30,000',
        step3: '70,000',
        step4: '100,000',
      }
    : {
        step1: '5,000',
        step2: '10,000',
        step3: '30,000',
        step4: '70,000',
      };

export const getSelectedPlanProspects = (step, isVersionLocked) =>
  getStepWisePlanProspects(isVersionLocked)[`step${step}`];

export const getInitialStepValue = () => 1;

export const getCustomScalePlusDiscountPer = (step) => {
  const stepWiseDiscount = {
    step1: '20',
    step2: '20',
    step3: '20',
    step4: '20',
  };

  return stepWiseDiscount[`step${step}`];
};

export const getPlanIcon = (planName) => {
  const planIconMap = {
    [SubscriptionPlanTitle.LeadFinderEmailStarterMonthly]: Images.RocketColor,
    [SubscriptionPlanTitle.LeadFinderEmailProMonthly]: Images.HeartColor,
    [SubscriptionPlanTitle.LeadFinderEmailScaleMonthly]: Images.StarColor,
    [SubscriptionPlanTitle.LeadFinderEmailScalePlus0Monthly]:
      Images.BuildingColor,
    [SubscriptionPlanTitle.LeadFinderEmailScalePlus1Monthly]:
      Images.BuildingColor,
    [SubscriptionPlanTitle.LeadFinderEmailScalePlus2Monthly]:
      Images.BuildingColor,
    [SubscriptionPlanTitle.LeadFinderEmailScalePlus3Monthly]:
      Images.BuildingColor,
    [SubscriptionPlanTitle.LeadFinderEmailScalePlus4Monthly]:
      Images.BuildingColor,
    [SubscriptionPlanTitle.LeadFinderEmailStarterYearly]: Images.RocketColor,
    [SubscriptionPlanTitle.LeadFinderEmailProYearly]: Images.HeartColor,
    [SubscriptionPlanTitle.LeadFinderEmailScaleYearly]: Images.StarColor,
    [SubscriptionPlanTitle.LeadFinderEmailScalePlus0Yearly]:
      Images.BuildingColor,
    [SubscriptionPlanTitle.LeadFinderEmailScalePlus1Yearly]:
      Images.BuildingColor,
    [SubscriptionPlanTitle.LeadFinderEmailScalePlus2Yearly]:
      Images.BuildingColor,
    [SubscriptionPlanTitle.LeadFinderEmailScalePlus3Yearly]:
      Images.BuildingColor,
    [SubscriptionPlanTitle.LeadFinderEmailScalePlus4Yearly]:
      Images.BuildingColor,
    [SubscriptionPlanTitle.LeadFinderStarterMonthly]: Images.RocketColor,
    [SubscriptionPlanTitle.LeadFinderProMonthly]: Images.HeartColor,
    [SubscriptionPlanTitle.LeadFinderStarterAnnually]: Images.RocketColor,
    [SubscriptionPlanTitle.LeadFinderProAnnually]: Images.HeartColor,
  };

  return planIconMap[planName];
};
